/* Common Imports */

import { styled, useMediaQuery } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";

/* Component Imports */

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
	Alert,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Popover,
	Skeleton,
	Slide,
	Snackbar,
	SvgIcon,
	SwipeableDrawer,
	Typography,
	useTheme,
} from "@mui/material";

/* Icon Imports */

import {
	Bookmark,
	BookmarkBorderOutlined,
	CurrencyRupee,
	HomeWorkOutlined,
	LocationOnOutlined,
	MoreVert,
} from "@mui/icons-material";
// import CarpetAreaIcon from "../../../../../public/images/icons/config/carpet_area.svg";
// import CarpetAreaWhiteIcon from "../../../../../public/images/icons/config/carpet_area_white.svg";

/* Library Imports */

import { TransitionProps } from "@mui/material/transitions";
import { useRouter } from "next/router";
import CapitalizeFirstLetter from "../../../../../lib/CapitalizeFirstLetter";
import prepareAreaString from "../../../../../lib/PrepareAreaString";
import prepareConfigString from "../../../../../lib/PrepareConfigString";
import prepareConfigTitle from "../../../../../lib/PrepareConfigTitle";
import PreparePriceString from "../../../../../lib/PreparePriceString";
import { addSaveEntityBusinessProfileThunk } from "../../../../../redux-magic/thunks";
import ReportModal from "../../../../modals/ReportModal";
import PropertyHighlightItem from "./sub-components/PropertyHighlightItem";

// icon imports //

import { LoginFlowTriggers, createLoginFlow } from "../../../../../lib/auth/helpers";
import Balconies from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/balconies.svg";
import Bathrooms from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/bathrooms.svg";
import Bedrooms from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/bedrooms.svg";
import Facing from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/facing.svg";
import NoOfFloors from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/floors.svg";
import Furnishing from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/furnishing.svg";
import LandArea from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/land_area.svg";
import NoOfSeats from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/no_of_seats.svg";
import NoOfUnits from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/no_of_units.svg";
import PlotDimension from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/plot_dimension.svg";
import PricePerUnit from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/price_per_unit.svg";
import PropertyAge from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/property_age.svg";
import PropertyCategory from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/property_category.svg";
import PropertyStatus from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/property_status.svg";
import PropertyType from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/property_type.svg";
import SecurityDeposit from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/security_deposit.svg";
import SuperBuiltUpArea from "../../../../../public/images/icons/singleproperty/propertyHighlightsIcons/super_builtup_area.svg";

/* Styled Components */

const Container = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "flex-start",
	width: "100%",
	background: theme.palette.background.paper,
	borderRadius: "16px",
	gap: "0.35rem",
	padding: "0.875rem",
	[theme.breakpoints.up("sm")]: {
		padding: "1rem",
		gap: "0.75rem",
	},
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem",
	},
}));

const ShowMore = styled(Typography)(({ theme }) => ({
	margin: "0.25rem 0rem 0rem 0rem",
	width: "100%",
	fontWeight: "500",
	color: theme.palette.primary.main,
	textAlign: "center",
	fontSize: "0.85rem",
	cursor: "pointer",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
		margin: "0.5rem 0rem 0rem 0rem",
	},
}));

const PriceTransactionTypeContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	gap: "0.25rem",
	width: "100%",
}));

const TransactionTypeChipAndActionIconsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	gap: "0.75rem",
}));

const ActionIconsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	gap: "0.25rem",
}));

const RupeeIcon = styled(CurrencyRupee)(({ theme }) => ({
	margin: "0rem 0rem 0rem 0rem",
	fontWeight: 600,
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		margin: "0rem 0rem 0rem 0rem",
		fontSize: "1.5rem",
	},
}));

const PriceText = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	margin: "0rem 0rem 0rem 0rem",
	fontWeight: 600,
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1.5rem",
	},
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	width: "100%",
	padding: "0.5rem 0.75rem 0.5rem 0.75rem",
	gap: "0.5rem",
}));

const TextButton = styled(Typography)(({ theme }) => ({
	fontWeight: 500,
	fontSize: "0.875rem",
}));

const CustomChip = styled(Chip, {
	shouldForwardProp: (prop) => prop !== "transaction_type",
})<{ transaction_type: string }>(({ theme, transaction_type }) => ({
	height: "1.5rem",
	fontWeight: 500,
	fontSize: "0.75rem",
	letterSpacing: "0.02857em",
	borderRadius: "8px",
	[theme.breakpoints.up("sm")]: {
		height: "1.75rem",
		fontWeight: 400,
		fontSize: "0.875rem",
	},
	backgroundColor:
		transaction_type === "buy"
			? "#b7efc5"
			: transaction_type === "rent"
				? "#ffc6ac"
				: transaction_type === "resale"
					? "#caf0f8"
					: "#b7efc5",
	color:
		transaction_type === "buy"
			? "#1e4620"
			: transaction_type === "rent"
				? "#4c2708"
				: transaction_type === "resale"
					? "#212121"
					: "#1e4620",
}));

const TitleText = styled(Typography)(({ theme }) => ({
	fontWeight: 500,
	fontSize: "0.875rem",
	margin: "0rem 0rem 0rem 0.25rem",
	overflow: "wrap",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1.5rem",
	},
}));

const LocationContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "0.25rem",
}));

const LocationIcon = styled(LocationOnOutlined)(({ theme }) => ({
	color: theme.palette.primary.main,
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1.35rem",
	},
}));

const LocationText = styled(Typography)(({ theme }) => ({
	color: theme.palette.text.secondary,
	fontSize: "0.875rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
}));

const CardsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	rowGap: "1rem",
	columnGap: "1rem",
	width: "100%",
	flexWrap: "wrap",
	margin: "0.5rem 0rem 0rem 0rem",
}));

const Snack = styled(Snackbar)(({ theme }) => ({}));

const AmenitiesContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	// alignItems: "center",
	flexWrap: "wrap",
	gap: "0.5rem",
	[theme.breakpoints.down("sm")]: {
		justifyContent: "flex-start",
	},
}));

const PropertyAmenitiesCard = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	gap: "0.25rem",
	padding: "0.5rem",
	backgroundColor: "#FC801929",
	borderRadius: "1.5rem",
}));

const AmenityText = styled(Typography)(({ theme }) => ({
	color: "#6E3037",
	fontSize: "0.875rem",
	fontWeight: 400,
	letterSpacing: "0.4px",
	[theme.breakpoints.down("md1190")]: {
		fontSize: "0.75rem",
	},
}));

const ImgTag = styled("img")(({ theme }) => ({
	height: "1.25rem",
	width: "1.25rem",
	[theme.breakpoints.up("md1190")]: {
		height: "1.5rem",
		width: "1.5rem",
	},
}));

const AmenitiesModalCard = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "48%",
	padding: "0.75rem 1rem",
	// gap: "0.5rem",
	alignSelf: "stretch",
	borderRadius: "1rem",
	backgroundColor: "#FFF",
	height: "4.5rem",
	// boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px;",
}));

const CustomDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		[theme.breakpoints.down("sm")]: {
			borderRadius: "0rem",
			minHeight: "100%",
		},
		minHeight: "700px",
		maxHeight: "100%",
		borderRadius: "1rem",
		background: theme.palette.background.paper,
	},
}));

/* Dialog Transition */

const DialogTransition = React.forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement<any, any>;
	},
	ref: React.Ref<unknown>,
) {
	return (
		<Slide
			direction="up"
			ref={ref}
			{...props}
		/>
	);
});

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

interface property_tags_amenities_approvals {
	id: string;
	icon: string;
	label: string;
}

const PropertyHighlightsUserView = ({
	loading,
	property_id,
	property_title,
	location,
	transaction_type,
	property_category,
	property_type,
	property_subtype,
	price,
	price_per_unit,
	security_deposit,
	property_status,
	possession_date,
	property_age,
	unit_facing,
	land_facing,
	furnishing,
	bhk,
	bathrooms,
	balconies,
	no_of_seats,
	no_of_units,
	no_of_floors,
	plot_dimensions,
	area,
	profile_context,
	dispatch,
	session,
	saved_content,
	property_amenties,
	login_flow_global_state,
}: {
	loading: boolean;
	property_id: string | ObjectId;
	property_title: string;
	location: string;
	transaction_type: string;
	property_category: string;
	property_type: Array<string>;
	property_subtype: string;
	price: StateInterface["property"]["price"];
	price_per_unit: StateInterface["property"]["price_per_unit"];
	security_deposit: StateInterface["property"]["security_deposit"];
	property_status: Array<string>;
	possession_date: StateInterface["property"]["possession_date"];
	property_age: number;
	unit_facing: StateInterface["property"]["unit_facing"];
	land_facing: StateInterface["property"]["land_facing"];
	furnishing: Array<string>;
	bhk: StateInterface["property"]["bhk"];
	bathrooms: StateInterface["property"]["bathroom"];
	balconies: StateInterface["property"]["balconies"];
	no_of_seats: StateInterface["property"]["no_of_seats"];
	no_of_units: StateInterface["property"]["no_of_units"];
	no_of_floors: StateInterface["property"]["floors"];
	plot_dimensions: StateInterface["property"]["plot_dimensions"];
	area: StateInterface["property"]["area"];
	profile_context: StateInterface["profile_context"];
	dispatch: Function;
	session: any;
	saved_content: LocalPropertyInterface["saved_content"];
	property_amenties: Array<property_tags_amenities_approvals>;
	login_flow_global_state: StateInterface["login_flow_global_state"];
}) => {
	const theme = useTheme();

	const router = useRouter();

	const smDevice = useMediaQuery(theme.breakpoints.down("sm"));

	const numOfAmenitiesToShow: number = smDevice ? 3 : 5;

	const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);

	const popOpen = Boolean(anchorEl);

	const id = popOpen ? "spp_simple_popover_report_modal" : undefined;

	const [showMore, setShowMore] = React.useState(false);

	const [reportModal, setReportModal] = React.useState(false);

	const [openAmenities, setOpenAmenities] = React.useState<boolean>(false);

	const [bookmark, setBookmark] = React.useState(false);

	React.useEffect(() => {
		if (property_id) {
			if (session)
				fetch(`${process.env.PRODUCTION_API_URL}saved-by-current-user`, {
					method: "POST",
					headers: {
						"Content-Type": "text/plain",
					},
					body: JSON.stringify({
						property_id: property_id,
						profile_context: profile_context,
						entity_type: "property",
					}),
				})
					.then((res) => res.json())
					.then((data) => {
						setBookmark(data.payload.saved_by_current_user ? data.payload.saved_by_current_user : false);
					});
		}
		/* eslint-disable-next-line react-hooks/exhaustive-deps*/
	}, [property_id, profile_context]);

	const sortedAmenities = [...property_amenties].sort((a, b) => a.label.length - b.label.length);

	/* Report Modal */

	const handleReportModalOpen = () => {
		setReportModal(true);
	};

	const handleReportModalClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason !== "backdropClick") {
			setReportModal(false);
		}
	};

	const handleClick = (event: React.MouseEvent<Element>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const [snackopen, setSnackOpen] = React.useState(false);
	const [savesnackopen, setSaveSnackOpen] = React.useState(false);

	const handleCloseSnack = () => {
		setSnackOpen(false);
	};

	const handleCloseSaveSnack = () => {
		setSaveSnackOpen(false);
	};

	const pdfSubmit = async (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		event.preventDefault();
		let response = null;
		try {
			response = await (
				await fetch(
					encodeURI(
						(process.env.PRODUCTION_URL || "") +
							"properties/" +
							router.query.slug +
							"/pdf?creator_is_business_profile=" +
							encodeURIComponent(profile_context ? profile_context.is_business_profile : true) +
							"&creator_user_id=" +
							encodeURIComponent(profile_context ? profile_context.user_id.toString() : "65a12c1996bb5e31adfea761") +
							"&creator_business_profile_id=" +
							encodeURIComponent(
								profile_context ? profile_context.business_profile_id.toString() : "65a12c1996bb5e31adfea761",
							) +
							"&locale=" +
							router.locale,
					),
					{
						method: "GET",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
					},
				)
			).blob();

			const a = document.createElement("a");
			a.href = URL.createObjectURL(response);
			a.download = router.query.slug + ".pdf";
			a.click();
			a.remove();
		} catch (error) {
			console.error("Error generating PDF:", error);
		}
		setSnackOpen(true);
	};

	React.useEffect(() => {
		window.addEventListener("scroll", handleClose, { passive: true });

		return () => {
			window.removeEventListener("scroll", handleClose);
		};
	}, []);

	const localLang = {
		propertyCategory: router.locale === "kn-IN" ? "ಕ್ಯಾಟಗರಿ" : router.locale === "ar-AE" ? "فئة" : "Category",
		propertyType: router.locale === "kn-IN" ? "ಟೈಪ್" : router.locale === "ar-AE" ? "نوع" : "Type",
		propertySubtype: router.locale === "kn-IN" ? "ಉಪ ಪ್ರಕಾರ" : router.locale === "ar-AE" ? "النوع الفرعي" : "Subtype",
		price_per_sqft:
			router.locale === "kn-IN" ? "ಬೆಲೆ / ಚದರ ಅಡಿ" : router.locale === "ar-AE" ? "السعر / قدم مربع" : "Price / sqft",
		securityDeposit:
			router.locale === "kn-IN"
				? "ಸಿಕ್ಯೂರಿಟಿ ಡೆಪಾಸಿಟ್"
				: router.locale === "ar-AE"
					? "عربون الأمان"
					: "Security deposit",
		propertyAge:
			router.locale === "kn-IN" ? "ಪ್ರಾಪರ್ಟಿ ವಯಸ್ಸು" : router.locale === "ar-AE" ? "عمر العقار" : "Property age",
		years: router.locale === "kn-IN" ? "ವರ್ಷಗಳು" : router.locale === "ar-AE" ? "سنوات" : "Years",
		facing: router.locale === "kn-IN" ? "ದಿಕ್ಕು" : router.locale === "ar-AE" ? "الواجهة" : "Facing",
		furnishing: router.locale === "kn-IN" ? "ಫರ್ನಿಶಿಂಗ್" : router.locale === "ar-AE" ? "التأثيث" : "Furnishing",
		bhk: router.locale === "kn-IN" ? "ಬಿಎಚ್ಕೆ" : router.locale === "ar-AE" ? "عدد الغرف" : "BHK",
		bathrooms: router.locale === "kn-IN" ? "ಬಾತ್ ರೂಮ್ಸ್" : router.locale === "ar-AE" ? "عدد الحمامات" : "Bathrooms",
		balconies: router.locale === "kn-IN" ? "ಬಾಲ್ಕನಿಗಳು" : router.locale === "ar-AE" ? "عدد الشرفات" : "Balconies",
		noOfSeats: router.locale === "kn-IN" ? "ಸೀಟುಗಳ ಸಂಖ್ಯೆ" : router.locale === "ar-AE" ? "عدد المقاعد" : "No of seats",
		totalNoOfUnits:
			router.locale === "kn-IN" ? "ಯುನಿಟ್ಗಳ ಸಂಖ್ಯೆ" : router.locale === "ar-AE" ? "عدد الوحدات" : "No of units",
		totalNoOfFloors:
			router.locale === "kn-IN"
				? "ಒಟ್ಟು ಮಹಡಿಗಳ ಸಂಖ್ಯೆ"
				: router.locale === "ar-AE"
					? "إجمالي عدد الطوابق"
					: "Total floors",
		plotDimensions:
			router.locale === "kn-IN"
				? "ಪ್ಲಾಟ್ ಡೈಮೆನ್ಷನ್ಸ್"
				: router.locale === "ar-AE"
					? "أبعاد قطعة الأرض"
					: "Plot dimensions",

		furnished: router.locale === "kn-IN" ? "ಫರ್ನಿಶ್ಡ್" : router.locale === "ar-AE" ? "مفروشة" : "Furnished",
		semi_furnished:
			router.locale === "kn-IN" ? "ಸೆಮಿ ಫರ್ನಿಶ್ಡ್" : router.locale === "ar-AE" ? "شبه مفروشة" : "Semi Furnished",
		unfurnished: router.locale === "kn-IN" ? "ಅನ್ಫರ್ನಿಶ್ಡ್" : router.locale === "ar-AE" ? "غير مفروشة" : "Unfurnished",
		bedrooms: router.locale === "kn-IN" ? "ಬೆಡ್ರೂಮ್ಗಳು" : router.locale === "ar-AE" ? "عدد الغرف" : "Bedrooms",
		sqft: router.locale === "kn-IN" ? "ಚದರ ಅಡಿ" : router.locale === "ar-AE" ? "قدم مربع" : "sqft",
		acre: router.locale === "kn-IN" ? "ಎಕರೆ" : router.locale === "ar-AE" ? "فدان" : "acre",
		sqm: router.locale === "kn-IN" ? "ಚದರ ಮೀಟರ್" : router.locale === "ar-AE" ? "متر مربع" : "sqm",
		land_area: router.locale === "kn-IN" ? "ಭೂಮಿಯ ವಿಸ್ತೀರ್ಣ" : router.locale === "ar-AE" ? "مساحة الأرض" : "Land area",
		unit: router.locale === "kn-IN" ? "ಯುನಿಟ್" : router.locale === "ar-AE" ? "وحدة القياس" : "Unit",
		area: router.locale === "kn-IN" ? "ವಿಸ್ತೀರ್ಣ" : router.locale === "ar-AE" ? "منطقة" : "Area",
		plot_area:
			router.locale === "kn-IN" ? "ಪ್ಲಾಟ್ ವಿಸ್ತೀರ್ಣ" : router.locale === "ar-AE" ? "مساحة قطعة الأرض" : "Plot area",
		built_up_area:
			router.locale === "kn-IN" ? "ನಿರ್ಮಿತ ವಿಸ್ತೀರ್ಣ" : router.locale === "ar-AE" ? "مساحة البناء" : "Built up area",
		property_status:
			router.locale === "kn-IN"
				? property_status.includes("Under construction") ||
					property_status.includes("under_construction") ||
					property_status.includes("Select date") ||
					property_status.includes("select_date")
					? transaction_type === "rent"
						? "ಲಭ್ಯತೆ"
						: "ಸಂಪತ್ತಿನ ಸ್ಥಿತಿ"
					: transaction_type === "rent"
						? "ಲಭ್ಯತೆ"
						: "ಸಂಪತ್ತಿನ ಸ್ಥಿತಿ"
				: router.locale === "ar-AE"
					? property_status.includes("Under construction") ||
						property_status.includes("under_construction") ||
						property_status.includes("Select date") ||
						property_status.includes("select_date")
						? transaction_type === "rent"
							? "متاح من"
							: "تاريخ الاستلام"
						: transaction_type === "rent"
							? "التوافر"
							: "حالة العقار"
					: property_status.includes("Under construction") ||
						  property_status.includes("under_construction") ||
						  property_status.includes("Select date") ||
						  property_status.includes("select_date")
						? transaction_type === "rent"
							? "Available from"
							: "Possession date"
						: transaction_type === "rent"
							? "Availability"
							: "Property status",
		seeMore: router.locale === "kn-IN" ? "ಮತ್ತಷ್ಟು ತೋರಿಸಿ" : router.locale === "ar-AE" ? "رؤية المزيد" : "See more",
		seeLess: router.locale === "kn-IN" ? "ಕಡಿಮೆ ತೋರಿಸಿ" : router.locale === "ar-AE" ? "رؤية أقل" : "See less",
		save: router.locale === "kn-IN" ? "ಉಳಿಸಿ" : router.locale === "ar-AE" ? "حفظ" : "Save",
		share: router.locale === "kn-IN" ? "ಹಂಚಿಕೊಳ್ಳಿ" : router.locale === "ar-AE" ? "شارك" : "Share",
		report: router.locale === "kn-IN" ? "ವರದಿ" : router.locale === "ar-AE" ? "أبلغ" : "Report",
		pdfdownload: router.locale === "kn-IN" ? "ಪಿಡಿಎಫ್ ಡೌನ್‌ಲೋಡ್" : router.locale === "ar-AE" ? "" : "Download PDF",
		amenities: router.locale === "kn-IN" ? "ಸೌಲಭ್ಯಗಳು" : router.locale === "ar-AE" ? "المرافق" : "Amenities",
		pdf_downloaded_successfully:
			router.locale === "kn-IN"
				? "ಪಿಡಿಎಫ್ ಯಶಸ್ವಿಯಾಗಿ ಡೌನ್‌ಲೋಡ್ ಮಾಡಲಾಗಿದೆ!"
				: router.locale === "ar-AE"
					? "تم تنزيل ملف بي دي إف بنجاح!"
					: "PDF downloaded successfully!",
		property_saved_successfully:
			router.locale === "kn-IN"
				? "ಸ್ವತಃ ಸಂರಕ್ಷಿತವಾಗಿದೆ!"
				: router.locale === "ar-AE"
					? "تم حفظ العقار بنجاح!"
					: "Property saved succesfully!",
	};

	if (loading) {
		return (
			<Container>
				<PriceTransactionTypeContainer>
					{/* Price */}

					<Skeleton
						variant="text"
						animation="wave"
						width="10rem"
						height="3rem"
					/>

					{/* Transaction Type */}

					<Skeleton
						variant="text"
						animation="wave"
						width="5rem"
						height="2rem"
					/>
				</PriceTransactionTypeContainer>

				{/* Title */}

				<Skeleton
					variant="text"
					animation="wave"
					width="20rem"
					height="2.25rem"
				/>

				{/* Location */}

				<Skeleton
					variant="text"
					animation="wave"
					width="10rem"
				/>

				{/* Cards Container */}

				<CardsContainer>
					<PropertyHighlightItem
						loading={loading}
						title="Type"
						icon={
							<HomeWorkOutlined
								sx={{
									color: theme.palette.icon.light,
									fontSize: "1.125rem",
									[theme.breakpoints.up("sm")]: {
										fontSize: "1.5rem",
									},
								}}
							/>
						}
						value={property_type ? property_type[0] : ""}
					/>
					<PropertyHighlightItem
						loading={loading}
						title="Type"
						icon={
							<HomeWorkOutlined
								sx={{
									color: theme.palette.icon.light,
									fontSize: "1.125rem",
									[theme.breakpoints.up("sm")]: {
										fontSize: "1.5rem",
									},
								}}
							/>
						}
						value={property_type ? property_type[0] : ""}
					/>
					<PropertyHighlightItem
						loading={loading}
						title="Type"
						icon={
							<HomeWorkOutlined
								sx={{
									color: theme.palette.icon.light,
									fontSize: "1.125rem",
									[theme.breakpoints.up("sm")]: {
										fontSize: "1.5rem",
									},
								}}
							/>
						}
						value={property_type ? property_type[0] : ""}
					/>
					<PropertyHighlightItem
						loading={loading}
						title="Type"
						icon={
							<HomeWorkOutlined
								sx={{
									color: theme.palette.icon.light,
									fontSize: "1.125rem",
									[theme.breakpoints.up("sm")]: {
										fontSize: "1.5rem",
									},
								}}
							/>
						}
						value={property_type ? property_type[0] : ""}
					/>
				</CardsContainer>
			</Container>
		);
	}

	const signIn = () => {
		if ((login_flow_global_state?.ui.type || "redirect") === "modal") {
			if (session && session.authorized) {
				new Function(...(login_flow_global_state?.action.sign_in || ["session", "return"])).call(session);
			} else {
				const { startFlow, resetFlow }: LoginFlowTriggers = createLoginFlow({
					dispatch,
					trigger_source: "navbar-dropdown",
					flow_type: "modal",
					autofill: false,
					action: {
						callback_url:
							process.env.PRODUCTION_API_URL +
							"auth/end-session?sessionId=" +
							encodeURIComponent(session?.session_id || "") +
							"&callbackUrl=" +
							encodeURIComponent(router.asPath),
						sign_in: (session: any) => {},
						sign_up: (session: any) => {},
						sign_out: () => {},
					},
				});
				startFlow();
			}
		} else {
		}
	};

	const submit = async () => {
		if (session === null) {
			signIn();
		} else {
			dispatch(
				addSaveEntityBusinessProfileThunk({
					is_business_profile: profile_context.is_business_profile,
					user_id: profile_context.user_id,
					business_profile_id: profile_context.business_profile_id,
					entity_id: property_id,
					entity_type: "property",
					add_remove: !bookmark ? "add" : "remove",
					url: router.asPath ? router.asPath : "",
					sessionId: session && session.session_id,
				}),
			);
			setBookmark(!bookmark);
			if (!bookmark) {
				setSaveSnackOpen(true);
			}
		}
	};

	const handleClickCloseAmenities = () => {
		setOpenAmenities(false);
	};

	return (
		<Container>
			{/* Snackbars */}

			<Snack
				open={snackopen}
				autoHideDuration={3000}
				onClose={handleCloseSnack}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={handleCloseSnack}
					severity="success"
					variant="filled"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
				>
					{localLang.pdf_downloaded_successfully}
				</Alert>
			</Snack>
			<Snack
				open={savesnackopen}
				autoHideDuration={3000}
				onClose={handleCloseSaveSnack}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={handleCloseSaveSnack}
					severity="success"
					variant="filled"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
				>
					{localLang.property_saved_successfully}
				</Alert>
			</Snack>

			{/* Report Modal */}

			<ReportModal
				open={reportModal}
				handleClose={handleReportModalClose}
				session={session}
				dispatch={dispatch}
				title="property"
			/>

			<PriceTransactionTypeContainer>
				{/* Price */}

				<PriceText variant="h3">
					<RupeeIcon />

					{price !== undefined
						? price
							? price.min === price.max
								? PreparePriceString(price.max)
								: PreparePriceString(price.min) + " - " + PreparePriceString(price.max)
							: "Price not available"
						: ""}
				</PriceText>

				<TransactionTypeChipAndActionIconsContainer>
					{/* Transaction Type */}

					<CustomChip
						label={
							transaction_type &&
							(transaction_type === "buy"
								? router.locale === "kn-IN"
									? "ಮಾರಾಟ"
									: router.locale === "ar-AE"
										? "بيع"
										: "Sale"
								: transaction_type === "rent"
									? router.locale === "kn-IN"
										? "ಬಾಡಿಗೆಗೆ"
										: router.locale === "ar-AE"
											? "إيجار"
											: "Rent"
									: transaction_type === "resale"
										? router.locale === "kn-IN"
											? "ಮರುಮಾರಾಟ"
											: router.locale === "ar-AE"
												? "إعادة بيع"
												: "Resale"
										: transaction_type === "ofb"
											? router.locale === "kn-IN"
												? ""
												: router.locale === "ar-AE"
													? "أو إف بي"
													: "OFB"
											: transaction_type.charAt(0).toUpperCase() + transaction_type.slice(1))
						}
						transaction_type={transaction_type}
					/>
					<ActionIconsContainer>
						{/* Save */}

						<IconButton
							disableFocusRipple
							disableRipple
							disableTouchRipple
							onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
								submit();
							}}
							sx={{
								padding: "0rem",
								"&:hover": {
									background: "none",
								},
							}}
						>
							{!bookmark ? (
								<BookmarkBorderOutlined
									sx={{
										color: theme.palette.primary.main,
										fontSize: smDevice ? "1.75rem" : "1.75rem",
									}}
								/>
							) : (
								<Bookmark
									sx={{
										color: theme.palette.primary.main,
										fontSize: smDevice ? "1.75rem" : "1.75rem",
									}}
								/>
							)}
						</IconButton>

						{/* More Options */}

						<IconButton
							onClick={handleClick}
							sx={{
								padding: "0rem",
							}}
						>
							<MoreVert
								sx={{
									width: "1.25rem",
									height: "1.25rem",
								}}
							/>
						</IconButton>

						<Popover
							id={id}
							open={popOpen}
							anchorEl={anchorEl}
							disableScrollLock={true}
							onClose={handleClose}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "right",
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "right",
							}}
							slotProps={{
								paper: {
									sx: {
										margin: "1rem 0rem 1rem 0rem",
										height: "auto",
										borderRadius: "16px",
										background: theme.palette.background.paper,
										direction: router.locale === "ar-AE" ? "rtl" : "ltr",
										[theme.breakpoints.down("xs")]: {
											width: 300,
											height: 440,
										},
									},
								},
							}}
						>
							<ContentContainer
								sx={{
									cursor: "pointer",
								}}
								onClick={handleReportModalOpen}
							>
								<TextButton>{localLang.report}</TextButton>
							</ContentContainer>

							<ContentContainer
								sx={{
									cursor: "pointer",
								}}
								onClick={pdfSubmit}
							>
								<TextButton>{localLang.pdfdownload}</TextButton>
							</ContentContainer>
						</Popover>
					</ActionIconsContainer>
				</TransactionTypeChipAndActionIconsContainer>
			</PriceTransactionTypeContainer>

			{/* Title */}

			<TitleText variant="h1">{property_title}</TitleText>

			{/* Location */}

			<LocationContainer
				onClick={() => {
					const mapElement = document.getElementById("map");
					if (mapElement) {
						mapElement.scrollIntoView({ behavior: "smooth" });
					}
				}}
			>
				<LocationIcon />
				<LocationText variant="h2">{location}</LocationText>
			</LocationContainer>
			<AmenitiesContainer sx={{ padding: "0.5rem" }}>
				{sortedAmenities
					.slice(0, numOfAmenitiesToShow)
					.map((item: property_tags_amenities_approvals, index: number) => (
						<PropertyAmenitiesCard
							key={index}
							onClick={() => setOpenAmenities(true)}
						>
							<ImgTag
								src={"/images/icons/singleproperty/amenities/" + item.label.toLowerCase().replace(/ /g, "_") + ".svg"}
								referrerPolicy="no-referrer"
							/>
							<AmenityText variant="body1">{item.label}</AmenityText>
						</PropertyAmenitiesCard>
					))}
				{sortedAmenities.length > 6 && (
					<PropertyAmenitiesCard onClick={() => setOpenAmenities(true)}>
						<AmenityText variant="body1">+{sortedAmenities.length - 5} more</AmenityText>
					</PropertyAmenitiesCard>
				)}
				{smDevice ? (
					<SwipeableDrawer
						anchor="bottom"
						open={openAmenities}
						onClose={handleClickCloseAmenities}
						onOpen={() => setOpenAmenities(true)}
						disableSwipeToOpen={true}
						PaperProps={{
							sx: {
								borderRadius: "16px 16px 0 0",
								background: theme.palette.background.paper,
								height: "70%",
								scrollbarWidth: "none",
							},
						}}
					>
						<DialogTitle
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								alignItems: "center",
								background: theme.palette.background.paper,
								position: "sticky",
								top: 0,
								zIndex: 1,
							}}
						>
							{localLang.amenities}
							<IconButton onClick={handleClickCloseAmenities}>
								<CloseOutlinedIcon />
							</IconButton>
						</DialogTitle>

						<DialogContent
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								alignItems: "flex-start",
								height: "100%",

								"&.MuiDialogContent-root": {
									padding: "1rem",
									flex: "none",
									background: theme.palette.background.default,
								},
							}}
						>
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "space-between",
									alignItems: "center",
									flexWrap: "wrap",
									gap: "0.5rem",
									rowGap: "1rem",
								}}
							>
								{sortedAmenities.map((item: property_tags_amenities_approvals, index: number) => (
									<AmenitiesModalCard key={index}>
										<AmenityText
											variant="body1"
											sx={{
												fontSize: "1rem",
												color: "#212121",
												[theme.breakpoints.down("sm")]: {
													fontSize: "0.875rem",
												},
											}}
										>
											{item.label}
										</AmenityText>
										<ImgTag
											src={
												"/images/icons/singleproperty/amenities/" + item.label.toLowerCase().replace(/ /g, "_") + ".svg"
											}
											referrerPolicy="no-referrer"
											sx={{ width: "2.25rem", height: "2.25rem" }}
										/>
									</AmenitiesModalCard>
								))}
							</div>
						</DialogContent>
					</SwipeableDrawer>
				) : (
					<CustomDialog
						open={openAmenities}
						onClose={handleClickCloseAmenities}
						fullWidth
						maxWidth="xs"
						TransitionComponent={DialogTransition}
						disableScrollLock={false}
						fullScreen={smDevice}
					>
						<DialogTitle
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								alignItems: "center",
								background: theme.palette.background.paper,
							}}
						>
							{localLang.amenities}
							<IconButton onClick={handleClickCloseAmenities}>
								<CloseOutlinedIcon />
							</IconButton>
						</DialogTitle>

						<DialogContent
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								alignItems: "flex-start",
								height: "100%",
								"&.MuiDialogContent-root": {
									padding: "1rem",
									flex: "none",
									background: theme.palette.background.paper,
								},
							}}
						>
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									justifyContent: "space-between",
									alignItems: "center",
									flexWrap: "wrap",
									gap: "0.5rem",
									rowGap: "1rem",
								}}
							>
								{sortedAmenities.map((item: property_tags_amenities_approvals, index: number) => (
									<AmenitiesModalCard key={index}>
										<AmenityText
											variant="body1"
											sx={{
												fontSize: "1rem",
												color: "#212121",
												[theme.breakpoints.down("sm")]: {
													fontSize: "0.875rem",
												},
											}}
										>
											{item.label}
										</AmenityText>
										<ImgTag
											src={
												"/images/icons/singleproperty/amenities/" + item.label.toLowerCase().replace(/ /g, "_") + ".svg"
											}
											referrerPolicy="no-referrer"
											sx={{ width: "2.25rem", height: "2.25rem" }}
										/>
									</AmenitiesModalCard>
								))}
							</div>
						</DialogContent>
					</CustomDialog>
				)}
			</AmenitiesContainer>
			<CardsContainer>
				{/* Property Type */}

				{transaction_type !== "" && property_category !== "" && property_type.length !== 0 ? (
					<PropertyHighlightItem
						loading={loading}
						title={localLang.propertyType}
						icon={
							<SvgIcon
								component={PropertyType}
								inheritViewBox={false}
								viewBox="0 0 38 38"
								width={24}
								height={24}
								sx={{
									width: "1.5rem",
									height: "1.5rem",
									fill: "none",
								}}
							/>
							// <HomeWorkOutlined
							// 	sx={{
							// 		color: theme.palette.icon.light,
							// 		fontSize: "1.125rem",
							// 		[theme.breakpoints.up("sm")]: {
							// 			fontSize: "1.5rem",
							// 		},
							// 	}}
							// />
						}
						value={property_type ? property_type[0] : ""}
					/>
				) : null}

				{/* Configuration */}

				{transaction_type !== "" && property_category !== "" && property_type.length !== 0 ? (
					<PropertyHighlightItem
						loading={loading}
						title={prepareConfigTitle(
							transaction_type as string,
							property_type as Array<string>,
							router.locale ? (router.locale as string) : "en-IN",
						)}
						icon={
							prepareConfigTitle(transaction_type, property_type) === "Bedrooms" ||
							prepareConfigTitle(transaction_type, property_type) === "Rooms" ||
							prepareConfigTitle(transaction_type, property_type) === "عدد الغرف" ||
							prepareConfigTitle(transaction_type, property_type) === "ಕೊಠಡಿಗಳು" ? (
								// <KingBedOutlined
								// 	sx={{
								// 		color: theme.palette.icon.light,
								// 		fontSize: "1.125rem",
								// 		[theme.breakpoints.up("sm")]: {
								// 			fontSize: "1.5rem",
								// 		},
								// 	}}
								// />
								<SvgIcon
									component={Bedrooms}
									inheritViewBox={false}
									viewBox="0 0 38 38"
									width={24}
									height={24}
									sx={{
										width: "1.5rem",
										height: "1.5rem",
										fill: "none",
									}}
								/>
							) : prepareConfigTitle(transaction_type, property_type) === "No of seats" ||
							  prepareConfigTitle(transaction_type, property_type) === "عدد المقاعد" ||
							  prepareConfigTitle(transaction_type, property_type) === "ಆಸನಗಳ ಸಂಖ್ಯೆ" ? (
								<SvgIcon
									component={NoOfSeats}
									inheritViewBox={false}
									viewBox="0 0 38 38"
									width={24}
									height={24}
									sx={{
										width: "1.5rem",
										height: "1.5rem",
										fill: "none",
									}}
								/>
							) : prepareConfigTitle(transaction_type, property_type) === "Land area" ||
							  prepareConfigTitle(transaction_type, property_type) === "مساحة الأرض" ||
							  prepareConfigTitle(transaction_type, property_type) === "ಭೂ ಪ್ರದೇಶ" ? (
								<SvgIcon
									component={LandArea}
									inheritViewBox={false}
									viewBox="0 0 38 38"
									width={24}
									height={24}
									sx={{
										width: "1.5rem",
										height: "1.5rem",
										fill: "none",
									}}
								/>
							) : (
								<SvgIcon
									component={SuperBuiltUpArea}
									inheritViewBox={false}
									viewBox="0 0 38 38"
									width={24}
									height={24}
									sx={{
										width: "1.5rem",
										height: "1.5rem",
										fill: "none",
									}}
								/>
							)
						}
						value={prepareConfigString(transaction_type, property_type, bhk, no_of_seats, area)}
					/>
				) : null}

				{/* Property Category */}

				{transaction_type !== "" ? (
					<PropertyHighlightItem
						loading={loading}
						title={localLang.propertyCategory}
						icon={
							// <CategoryOutlined
							// 	sx={{
							// 		color: theme.palette.icon.light,
							// 		fontSize: "1.125rem",
							// 		[theme.breakpoints.up("sm")]: {
							// 			fontSize: "1.5rem",
							// 		},
							// 	}}
							// />
							<SvgIcon
								component={PropertyCategory}
								inheritViewBox={false}
								viewBox="0 0 38 38"
								width={24}
								height={24}
								sx={{
									width: "1.5rem",
									height: "1.5rem",
									fill: "none",
								}}
							/>
						}
						value={CapitalizeFirstLetter(property_category)}
					/>
				) : null}

				{/* Property Status / Availability */}

				{transaction_type !== "" && property_category !== "" && property_type.length !== 0 ? (
					<PropertyHighlightItem
						loading={loading}
						title={localLang.property_status}
						icon={
							// <EventAvailableOutlined
							// 	sx={{
							// 		color: theme.palette.icon.light,
							// 		fontSize: "1.125rem",
							// 		[theme.breakpoints.up("sm")]: {
							// 			fontSize: "1.5rem",
							// 		},
							// 	}}
							// />
							<SvgIcon
								component={PropertyStatus}
								inheritViewBox={false}
								viewBox="0 0 38 38"
								width={24}
								height={24}
								sx={{
									width: "1.5rem",
									height: "1.5rem",
									fill: "none",
								}}
							/>
						}
						value={
							transaction_type === "buy" &&
							(property_type.includes("Land (Residential)") ||
								property_type.includes("Land (Commercial)") ||
								property_type.includes("Land (Agricultural)") ||
								property_type.includes("Land (Industrial)") ||
								property_type.includes("Mining"))
								? router.locale === "kn-IN"
									? ""
									: router.locale === "ar-AE"
										? "جاهز للتسجيل"
										: "Ready for registration"
								: property_status.includes("Under construction") ||
									  property_status.includes("under_construction") ||
									  property_status.includes("Select date") ||
									  property_status.includes("select_date")
									? new Date(possession_date).toLocaleDateString("en-IN")
									: property_status.map(
											(element: string) => element.charAt(0).toUpperCase() + element.slice(1).replace(/_/g, " "),
										)
						}
					/>
				) : null}

				{showMore && (
					<>
						{/* Area */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						prepareConfigTitle(transaction_type, property_type) !== "Land area" &&
						prepareConfigTitle(transaction_type, property_type) !== "Area" &&
						prepareConfigTitle(transaction_type, property_type) !== "مساحة الأرض" &&
						prepareConfigTitle(transaction_type, property_type) !== "مساحة" &&
						prepareConfigTitle(transaction_type, property_type) !== "ಭೂ ಪ್ರದೇಶ" &&
						prepareConfigTitle(transaction_type, property_type) !== "ಪ್ರದೇಶ" ? (
							<PropertyHighlightItem
								loading={loading}
								title={
									property_type.includes("Land (Residential)") ||
									property_type.includes("Land (Commercial)") ||
									property_type.includes("Land (Agricultural)") ||
									property_type.includes("Land (Industrial)") ||
									property_type.includes("Mining")
										? localLang.land_area
										: property_type.includes("Plot") ||
											  property_type.includes("Farm Plot") ||
											  property_type.includes("Plot (Commercial)")
											? localLang.plot_area
											: localLang.built_up_area
								}
								icon={
									property_type.includes("Land (Residential)") ||
									property_type.includes("Land (Commercial)") ||
									property_type.includes("Land (Agricultural)") ||
									property_type.includes("Land (Industrial)") ||
									property_type.includes("Mining") ? (
										// <SvgIcon
										// 	inheritViewBox={false}
										// 	viewBox="0 0 24 24"
										// 	width={24}
										// 	height={24}
										// 	sx={{
										// 		height: "1rem",
										// 		width: "1rem",
										// 		[theme.breakpoints.up("sm")]: {
										// 			height: "1.5rem",
										// 			width: "1.5rem",
										// 		},
										// 	}}
										// >
										// 	{theme.palette.mode === "dark" ? <LandAreaWhiteIcon /> : <LandAreaIcon />}
										// </SvgIcon>
										<SvgIcon
											component={LandArea}
											inheritViewBox={false}
											viewBox="0 0 38 38"
											width={24}
											height={24}
											sx={{
												width: "1.5rem",
												height: "1.5rem",
												fill: "none",
											}}
										/>
									) : (
										<SvgIcon
											component={SuperBuiltUpArea}
											inheritViewBox={false}
											viewBox="0 0 38 38"
											width={24}
											height={24}
											sx={{
												width: "1.5rem",
												height: "1.5rem",
												fill: "none",
											}}
										/>
										// <SvgIcon
										// 	inheritViewBox={false}
										// 	viewBox="0 0 24 24"
										// 	width={24}
										// 	height={24}
										// 	sx={{
										// 		height: "1rem",
										// 		width: "1rem",
										// 		[theme.breakpoints.up("sm")]: {
										// 			height: "1.5rem",
										// 			width: "1.5rem",
										// 		},
										// 	}}
										// >
										// 	{theme.palette.mode === "dark" ? <SuperBuiltAreaWhiteIcon /> : <SuperBuiltAreaIcon />}
										// </SvgIcon>
									)
								}
								value={prepareAreaString(
									area,
									property_type
										? property_type.includes("Land (Residential)") ||
											property_type.includes("Land (Commercial)") ||
											property_type.includes("Land (Agricultural)") ||
											property_type.includes("Land (Industrial)") ||
											property_type.includes("Mining")
											? "land_area"
											: "super_built_up_area"
										: "super_built_up_area",
								)}
							/>
						) : null}

						{/* Property Subtype */}

						{/* {transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						(property_category == "commercial" ||
							property_category == "industrial" ||
							property_category == "warehousing") ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.propertySubtype}
								icon={
									// <DomainAddOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={PropertyType}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={property_subtype}
							/>
						) : null} */}

						{/* Price per unit */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						transaction_type === "rent" &&
						(property_type.includes("Co-living/PG") ||
							property_type.includes("Co-living/PG") ||
							property_type.includes("Plot") ||
							property_type.includes("Farm Plot") ||
							property_type.includes("Land (Residential)") ||
							property_type.includes("Land (Commercial)") ||
							property_type.includes("Land (Agricultural)") ||
							property_type.includes("Land (Industrial)") ||
							property_type.includes("Office Space") ||
							property_type.includes("Factory") ||
							property_type.includes("Mining") ||
							property_type.includes("Warehouse")) ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.price_per_sqft}
								icon={
									// <CurrencyRupeeOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={PricePerUnit}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={"₹" + price_per_unit.min.ft.toFixed(2).toString() + "/sqft"}
							/>
						) : null}

						{/* Security deposit */}

						{transaction_type == "rent" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						security_deposit.min > 0 ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.securityDeposit}
								icon={
									// <MonetizationOnOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={SecurityDeposit}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={"₹" + PreparePriceString(security_deposit.min)}
							/>
						) : null}

						{/* Property Age */}

						{transaction_type == "resale" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						property_age !== 0 &&
						!property_type.includes("Land (Residential)") &&
						!property_type.includes("Land (Commercial)") &&
						!property_type.includes("Land (Agricultural)") &&
						!property_type.includes("Land (Industrial)") &&
						!property_type.includes("Mining") &&
						!property_status.includes("Under construction") &&
						!property_status.includes("under_construction") &&
						!property_status.includes("Select date") &&
						!property_status.includes("select_date") ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.propertyAge}
								icon={
									// <HourglassEmptyOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={PropertyAge}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={property_age.toString() + " " + localLang.years}
							/>
						) : null}

						{/* Facing */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						router.locale !== "ar-AE" &&
						(land_facing.length !== 0 || unit_facing.length !== 0) ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.facing}
								icon={
									// <ExploreOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={Facing}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={
									property_type.includes("Land (Residential)") ||
									property_type.includes("Land (Commercial)") ||
									property_type.includes("Land (Agricultural)") ||
									property_type.includes("Land (Industrial)") ||
									property_type.includes("Warehouse") ||
									property_type.includes("Factory") ||
									property_type.includes("Mining")
										? land_facing
												.map((element: string) => element.charAt(0).toUpperCase() + element.slice(1).replace(/_/g, " "))
												.join(", ")
										: unit_facing
												.map((element: string) => element.charAt(0).toUpperCase() + element.slice(1).replace(/_/g, " "))
												.join(", ")
								}
							/>
						) : null}

						{/* Furnishing */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						furnishing.length !== 0 &&
						!property_type.includes("Plot") &&
						!property_type.includes("Farm Plot") &&
						!property_type.includes("Plot (Commercial)") &&
						!property_type.includes("Land (Residential)") &&
						!property_type.includes("Land (Commercial)") &&
						!property_type.includes("Land (Agricultural)") &&
						!property_type.includes("Land (Industrial)") &&
						!property_type.includes("Co-working") &&
						!property_type.includes("Warehouse") &&
						!property_type.includes("Factory") &&
						!property_type.includes("Mining") ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.furnishing}
								icon={
									// <ChairOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={Furnishing}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={furnishing
									.map((furnish: any) => furnish.charAt(0).toUpperCase() + furnish.slice(1).replace(/_/g, " "))
									.join(", ")}
							/>
						) : null}

						{/* Bedrooms */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						!property_type.includes("Plot") &&
						!property_type.includes("Farm Plot") &&
						!property_type.includes("Plot (Commercial)") &&
						!property_type.includes("Land (Residential)") &&
						!property_type.includes("Land (Commercial)") &&
						!property_type.includes("Land (Agricultural)") &&
						!property_type.includes("Land (Industrial)") &&
						!property_type.includes("Building") &&
						!property_type.includes("Shop") &&
						!property_type.includes("Office Space") &&
						!property_type.includes("Co-working") &&
						!property_type.includes("Warehouse") &&
						!property_type.includes("Factory") &&
						!property_type.includes("Mining") &&
						prepareConfigTitle(transaction_type, property_type) !== "Bedrooms" &&
						prepareConfigTitle(transaction_type, property_type) !== "Rooms" &&
						prepareConfigTitle(transaction_type, property_type) !== "عدد الغرف" &&
						prepareConfigTitle(transaction_type, property_type) !== "ಕೊಠಡಿಗಳು" ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.bhk}
								icon={
									// <KingBedOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={Bedrooms}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={
									bhk.min === bhk.max
										? bhk.max > 0
											? bhk.max.toString()
											: ""
										: (bhk.min > 0 ? bhk.min.toString() : "") + "-" + (bhk.max > 0 ? bhk.max.toString() : "")
								}
							/>
						) : null}

						{/* Bathrooms */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						bathrooms.min !== 0 &&
						!property_type.includes("Co-living/PG") &&
						!property_type.includes("Plot") &&
						!property_type.includes("Farm Plot") &&
						!property_type.includes("Plot (Commercial)") &&
						!property_type.includes("Land (Residential)") &&
						!property_type.includes("Land (Commercial)") &&
						!property_type.includes("Land (Agricultural)") &&
						!property_type.includes("Land (Industrial)") &&
						!property_type.includes("Building") &&
						!property_type.includes("Shop") &&
						!property_type.includes("Office Space") &&
						!property_type.includes("Co-working") &&
						!property_type.includes("Warehouse") &&
						!property_type.includes("Factory") &&
						!property_type.includes("Mining") ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.bathrooms}
								icon={
									// <BathtubOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={Bathrooms}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={
									bathrooms.min === bathrooms.max
										? bathrooms.max > 0
											? bathrooms.max.toString()
											: ""
										: (bathrooms.min > 0 ? bathrooms.min.toString() : "") +
											"-" +
											(bathrooms.max > 0 ? bathrooms.max.toString() : "")
								}
							/>
						) : null}

						{/* Balconies */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						balconies.min !== 0 &&
						!property_type.includes("Co-living/PG") &&
						!property_type.includes("Plot") &&
						!property_type.includes("Farm Plot") &&
						!property_type.includes("Plot (Commercial)") &&
						!property_type.includes("Land (Residential)") &&
						!property_type.includes("Land (Commercial)") &&
						!property_type.includes("Land (Agricultural)") &&
						!property_type.includes("Land (Industrial)") &&
						!property_type.includes("Building") &&
						!property_type.includes("Shop") &&
						!property_type.includes("Office Space") &&
						!property_type.includes("Co-working") &&
						!property_type.includes("Warehouse") &&
						!property_type.includes("Factory") &&
						!property_type.includes("Mining") ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.balconies}
								icon={
									// <BalconyOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={Balconies}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={
									balconies.min === balconies.max
										? balconies.max > 0
											? balconies.max.toString()
											: ""
										: (balconies.min > 0 ? balconies.min.toString() : "") +
											"-" +
											(balconies.max > 0 ? balconies.max.toString() : "")
								}
							/>
						) : null}

						{/* No of Seats */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						no_of_seats.min !== 0 &&
						property_type.includes("Co-working") &&
						prepareConfigTitle(transaction_type, property_type) !== "No of seats" &&
						prepareConfigTitle(transaction_type, property_type) !== "عدد المقاعد" &&
						prepareConfigTitle(transaction_type, property_type) !== "ಆಸನಗಳ ಸಂಖ್ಯೆ" ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.noOfSeats}
								icon={
									// <EventSeatOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={NoOfSeats}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={
									no_of_seats.min === no_of_seats.max
										? no_of_seats.max > 0
											? no_of_seats.max.toString()
											: ""
										: (no_of_seats.min > 0 ? no_of_seats.min.toString() : "") +
											"-" +
											(no_of_seats.max > 0 ? no_of_seats.max.toString() : "")
								}
							/>
						) : null}

						{/* No of Units */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						no_of_units.min !== 0 &&
						transaction_type == "buy" &&
						(property_type.includes("Apartment") ||
							property_type.includes("Plot") ||
							property_type.includes("Farm Plot") ||
							property_type.includes("Plot (Commercial)") ||
							property_type.includes("Villa") ||
							property_type.includes("Row House") ||
							property_type.includes("Builder Floor Apartment") ||
							property_type.includes("Studio Apartment") ||
							property_type.includes("Penthouse")) ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.totalNoOfUnits}
								icon={
									// <CorporateFareOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={NoOfUnits}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={
									no_of_units.min === no_of_units.max
										? no_of_units.max > 0
											? no_of_units.max.toString()
											: ""
										: (no_of_units.min > 0 ? no_of_units.min.toString() : "") +
											"-" +
											(no_of_units.max > 0 ? no_of_units.max.toString() : "")
								}
							/>
						) : null}

						{/* No of Floors */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						no_of_floors.min !== 0 &&
						!property_type.includes("Plot") &&
						!property_type.includes("Farm Plot") &&
						!property_type.includes("Plot (Commercial)") &&
						!property_type.includes("Land (Residential)") &&
						!property_type.includes("Land (Commercial)") &&
						!property_type.includes("Land (Agricultural)") &&
						!property_type.includes("Land (Industrial)") &&
						!property_type.includes("Warehouse") &&
						!property_type.includes("Factory") &&
						!property_type.includes("Mining") ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.totalNoOfFloors}
								icon={
									// <StairsOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={NoOfFloors}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={
									no_of_floors.min === no_of_floors.max
										? no_of_floors.max > 0
											? no_of_floors.max.toString()
											: ""
										: (no_of_floors.min > 0 ? no_of_floors.min.toString() : "") +
											"-" +
											(no_of_floors.max > 0 ? no_of_floors.max.toString() : "")
								}
							/>
						) : null}

						{/* Plot Dimensions */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						plot_dimensions.length !== 0 &&
						(property_type.includes("Plot") ||
							property_type.includes("Farm Plot") ||
							property_type.includes("Plot (Commercial)")) ? (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.plotDimensions}
								icon={
									// <AspectRatioOutlined
									// 	sx={{
									// 		color: theme.palette.icon.light,
									// 		fontSize: "1.125rem",
									// 		[theme.breakpoints.up("sm")]: {
									// 			fontSize: "1.5rem",
									// 		},
									// 	}}
									// />
									<SvgIcon
										component={PlotDimension}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={plot_dimensions
									.filter((dimension: any) => dimension !== "Other")
									.map((dimension: any) => dimension)
									.join(", ")}
							/>
						) : null}

						{/* Land Area */}

						{transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						transaction_type !== "buy" ? null : property_type.includes("Land (Residential)") ||
						  property_type.includes("Land (Commercial)") ||
						  property_type.includes("Land (Agricultural)") ||
						  property_type.includes("Land (Industrial)") ||
						  property_type.includes("Mining") ? null : (
							<PropertyHighlightItem
								loading={loading}
								title={localLang.land_area}
								icon={
									// <SvgIcon
									// 	inheritViewBox={false}
									// 	viewBox="0 0 24 24"
									// 	width={24}
									// 	height={24}
									// >
									// 	{theme.palette.mode === "dark" ? <LandAreaWhiteIcon /> : <LandAreaIcon />}
									// </SvgIcon>
									<SvgIcon
										component={LandArea}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={
									area.land_area.max.acre <= 0.5
										? area.land_area.max.ft.toFixed(0) + " sqft"
										: area.land_area.max.acre % 1 === 0
											? area.land_area.max.acre.toFixed(0) + " acre"
											: area.land_area.max.acre.toFixed(2) + " acre"
								}
							/>
						)}

						{/* Super Built Up Area */}

						{(transaction_type !== "" &&
							property_category !== "" &&
							property_type.length !== 0 &&
							!property_type.includes("Land (Residential)") &&
							!property_type.includes("Land (Commercial)") &&
							!property_type.includes("Land (Agricultural)") &&
							!property_type.includes("Land (Industrial)") &&
							!property_type.includes("Mining")) ||
						area.super_built_up_area.min.ft === 0 ? null : (
							<PropertyHighlightItem
								loading={loading}
								title={
									property_type.includes("Plot") ||
									property_type.includes("Farm Plot") ||
									property_type.includes("Plot (Commercial)")
										? localLang.plot_area
										: localLang.built_up_area
								}
								icon={
									// <SvgIcon
									// 	inheritViewBox={false}
									// 	viewBox="0 0 24 24"
									// 	width={24}
									// 	height={24}
									// >
									// 	{theme.palette.mode === "dark" ? <SuperBuiltAreaWhiteIcon /> : <SuperBuiltAreaIcon />}
									// </SvgIcon>
									<SvgIcon
										component={SuperBuiltUpArea}
										inheritViewBox={false}
										viewBox="0 0 38 38"
										width={24}
										height={24}
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											fill: "none",
										}}
									/>
								}
								value={
									area.super_built_up_area.min.ft === area.super_built_up_area.max.ft
										? area.super_built_up_area.max.ft > 0
											? area.super_built_up_area.max.ft.toFixed(0).toString() + " sqft"
											: ""
										: (area.super_built_up_area.min.ft > 0
												? area.super_built_up_area.min.ft.toFixed(0).toString() + " - "
												: "") +
											(area.super_built_up_area.max.ft > 0
												? area.super_built_up_area.max.ft.toFixed(0).toString() + " sqft"
												: "")
								}
							/>
						)}

						{/* Carpet Area */}

						{/* {transaction_type !== "" &&
						property_category !== "" &&
						property_type.length !== 0 &&
						!property_type.includes("Land (Residential)") &&
						!property_type.includes("Land (Commercial)") &&
						!property_type.includes("Land (Agricultural)") &&
						!property_type.includes("Land (Industrial)") &&
						!property_type.includes("Plot") &&
						!property_type.includes("Farm Plot") &&
						!property_type.includes("Plot (Commercial)") &&
						!property_type.includes("Co-living/PG") &&
						!property_type.includes("Warehouse") &&
						!property_type.includes("Factory") &&
						!property_type.includes("Mining") &&
						area.carpet_area.min.ft > 0 &&
						area.carpet_area.max.ft ? (
							<PropertyHighlightItem
								loading={loading}
								title="Carpet area"
								icon={
									<SvgIcon
										inheritViewBox={false}
										viewBox="0 0 24 24"
										width={24}
										height={24}
									>
										{theme.palette.mode === "dark" ? <CarpetAreaWhiteIcon /> : <CarpetAreaIcon />}
									</SvgIcon>
								}
								value={
									area.carpet_area.min.ft === area.carpet_area.max.ft
										? area.carpet_area.max.ft > 0
											? area.carpet_area.max.ft.toFixed(0).toString() + " sqft"
											: ""
										: (area.carpet_area.min.ft > 0 ? area.carpet_area.min.ft.toFixed(0).toString() + " - " : "") +
											(area.carpet_area.max.ft > 0 ? area.carpet_area.max.ft.toFixed(0).toString() + " sqft" : "")
								}
							/>
						) : null} */}
					</>
				)}
			</CardsContainer>

			{/* Show More Button */}

			<ShowMore
				variant="body2"
				textAlign={"center"}
				onClick={() => setShowMore(!showMore)}
			>
				{showMore ? localLang.seeLess : localLang.seeMore}
			</ShowMore>
		</Container>
	);
};

export default PropertyHighlightsUserView;
